import React, { useEffect, useState } from 'react';
import { getMe, updateEmail, updatePassword } from './api';

// Master-admin account settings: view info, change email, change password.
export default function AdminSettings() {
  const [me, setMe] = useState(null);
  const [emailForm, setEmailForm] = useState({ current_password: '', new_email: '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [emailMsg, setEmailMsg] = useState(null); // { ok, text }
  const [pwMsg, setPwMsg] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((res) => { if (!cancelled) setMe(res.data); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailMsg(null);
    setSavingEmail(true);
    try {
      const res = await updateEmail(emailForm.current_password, emailForm.new_email);
      setEmailMsg({ ok: true, text: `Email changed to ${res.data.email}. Use it next time you sign in.` });
      setEmailForm({ current_password: '', new_email: '' });
      setMe((m) => (m ? { ...m, user: { ...m.user, email: res.data.email } } : m));
    } catch (err) {
      setEmailMsg({ ok: false, text: err.response?.data?.error || 'Could not change email' });
    }
    setSavingEmail(false);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.new_password !== pwForm.confirm) {
      setPwMsg({ ok: false, text: 'New passwords do not match' });
      return;
    }
    setSavingPw(true);
    try {
      await updatePassword(pwForm.current_password, pwForm.new_password);
      setPwMsg({ ok: true, text: 'Password updated.' });
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      setPwMsg({ ok: false, text: err.response?.data?.error || 'Could not change password' });
    }
    setSavingPw(false);
  };

  const email = me?.user?.email || '—';

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={styles.card}>
        <div style={styles.cardTitle}>Your account</div>
        <div style={styles.infoRow}><span style={styles.infoLabel}>Email</span><span style={styles.infoValue}>{email}</span></div>
        <div style={styles.infoRow}><span style={styles.infoLabel}>Role</span><span style={styles.infoValue}>Master admin</span></div>
      </div>

      <form onSubmit={submitEmail} style={styles.card}>
        <div style={styles.cardTitle}>Change email</div>
        {emailMsg && (
          <div
            style={emailMsg.ok ? styles.ok : styles.err}
            role={emailMsg.ok ? 'status' : 'alert'}
            aria-live={emailMsg.ok ? 'polite' : 'assertive'}
          >
            {emailMsg.text}
          </div>
        )}
        <div style={styles.field}>
          <label htmlFor="admin-email-current-pw" style={styles.label}>Current password</label>
          <input id="admin-email-current-pw" style={styles.input} type="password" required value={emailForm.current_password}
            onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} />
        </div>
        <div style={styles.field}>
          <label htmlFor="admin-email-new" style={styles.label}>New email</label>
          <input id="admin-email-new" style={styles.input} type="email" required value={emailForm.new_email}
            onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} />
        </div>
        <div style={styles.caution}>
          <span aria-hidden="true">⚠ </span>If you change this, also update <code style={styles.code}>ADMIN_EMAILS</code> in your
          hosting (Railway) to the new address, or you'll lose admin access.
        </div>
        <button type="submit" disabled={savingEmail} style={styles.saveBtn}>
          {savingEmail ? 'Saving…' : 'Update email'}
        </button>
      </form>

      <form onSubmit={submitPassword} style={styles.card}>
        <div style={styles.cardTitle}>Change password</div>
        {pwMsg && (
          <div
            style={pwMsg.ok ? styles.ok : styles.err}
            role={pwMsg.ok ? 'status' : 'alert'}
            aria-live={pwMsg.ok ? 'polite' : 'assertive'}
          >
            {pwMsg.text}
          </div>
        )}
        <div style={styles.field}>
          <label htmlFor="admin-pw-current" style={styles.label}>Current password</label>
          <input id="admin-pw-current" style={styles.input} type="password" required value={pwForm.current_password}
            onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} />
        </div>
        <div style={styles.field}>
          <label htmlFor="admin-pw-new" style={styles.label}>New password</label>
          <input id="admin-pw-new" style={styles.input} type="password" required minLength={8} value={pwForm.new_password}
            onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} />
        </div>
        <div style={styles.field}>
          <label htmlFor="admin-pw-confirm" style={styles.label}>Confirm new password</label>
          <input id="admin-pw-confirm" style={styles.input} type="password" required minLength={8} value={pwForm.confirm}
            onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
        </div>
        <button type="submit" disabled={savingPw} style={styles.saveBtn}>
          {savingPw ? 'Saving…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}

const styles = {
  card: { background: '#fff', border: '1px solid #e3e9df', borderRadius: 14, padding: 22, marginBottom: 16 },
  cardTitle: { fontFamily: 'Manrope, sans-serif', fontSize: 16, fontWeight: 800, color: '#1a1a1a', marginBottom: 14, letterSpacing: '-0.2px' },
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0f2ec' },
  infoLabel: { fontSize: 13, color: '#5f6b58', fontWeight: 600 },
  infoValue: { fontSize: 13.5, color: '#1a1a1a', fontWeight: 600 },
  field: { display: 'flex', flexDirection: 'column', marginBottom: 12 },
  label: { fontSize: 12, fontWeight: 600, color: '#5f6b58', marginBottom: 6 },
  input: { width: '100%', padding: '11px 13px', border: '1px solid #e3e9df', borderRadius: 9, fontSize: 14, color: '#1a1a1a', background: '#f7f9f4', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  caution: { fontSize: 12, color: '#92400E', background: '#FEF3C7', borderRadius: 8, padding: '9px 11px', marginBottom: 12, lineHeight: 1.45 },
  code: { fontFamily: 'ui-monospace, SFMono-Regular, monospace', background: '#fff7e6', padding: '1px 5px', borderRadius: 4, border: '1px solid #f5deb0' },
  saveBtn: { padding: '11px 18px', border: 'none', background: '#14653a', borderRadius: 9, fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  ok: { background: '#ecf8ee', color: '#14653a', border: '1px solid #cfe9d4', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 14 },
  err: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 14 }
};
